import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { LanguageService } from '../../shared/services/language.service';
import { NotificationsService } from '../../shared/services/notifications.service';
import { Notice } from 'src/app/models/Notice';
import { NotificationType, NotificationTypeLabels } from 'src/app/models/NotificationType';

@Component({
  standalone: true,
  imports: [CommonModule, MatIconModule],
  selector: 'app-notification-detail-dialog',
  templateUrl: './notification-detail-dialog.component.html',
  styleUrls: ['./notification-detail-dialog.component.css']
})
export class NotificationDetailDialogComponent implements OnInit {

  variables: any;
  updated: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public notification: Notice,
    private dialogRef: MatDialogRef<NotificationDetailDialogComponent>,
    @Inject(NotificationsService) private notificationsService: NotificationsService,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.languageService.translations$.subscribe((vars: any) => {
      this.variables = vars;
    });

    if (!this.notification.readDate) {
      this.notificationsService.markAsReadApi(this.notification.id).subscribe({
        next: () => {
          this.notification.readDate = new Date().toISOString();
          this.refreshUnreadNotifications();
          this.updated = true;
        },
        error: () => {
          // Keep dialog working even if read-mark fails.
        }
      });
    }
  }

  close(): void {
    this.dialogRef.close(this.updated);

    this.refreshUnreadNotifications();
  }

  private refreshUnreadNotifications() {
    this.notificationsService.refreshUnreadCountNow(this.notification.userId).subscribe({
      next: () => {},
      error: (notificationError) => console.error('Error fetching unread count:', notificationError)
    });
  }

  getPriorityLabel(priority: number): string {
    const labels: { [key: number]: string } = {
      1: this.variables?.priorityVeryHigh || 'Muy alta',
      2: this.variables?.priorityHigh || 'Alta',
      3: this.variables?.priorityMedium || 'Media',
      4: this.variables?.priorityLow || 'Baja',
      5: this.variables?.priorityVeryLow || 'Muy baja'
    };

    return labels[priority] || '-';
  }

  formatReadDate(value: string | null | undefined): string {
    if (!value) return '';
    const alreadyFormatted = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(value);
    if (alreadyFormatted) return value;
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  getTypeMeta(eventType: string | number): { code: string; label: string; icon: string } | undefined {
    if (typeof eventType === 'number') {
      return NotificationTypeLabels[eventType as NotificationType];
    }

    return Object.values(NotificationTypeLabels).find(meta => meta.code === String(eventType));
  }

  getTranslatedTypeLabel(typeCode: string, fallbackLabel: string | number): string {
    const keyByCode: Record<string, string> = {
      ESTIMATION_TO_CERTIFICATE: 'notificationTypePendingCertification',
      REVIEW_ADDED: 'notificationTypeReviewAdded',
      ESTIMATION_APPROVED: 'notificationTypeApproved',
      ESTIMATION_REJECTED: 'notificationTypeRejected',
      ESTIMATION_TO_FIX: 'notificationTypeToFix'
    };

    const translationKey = keyByCode[typeCode];
    const normalizedFallbackLabel = String(fallbackLabel);

    if (!translationKey) {
      return normalizedFallbackLabel;
    }

    return this.variables?.[translationKey] || normalizedFallbackLabel;
  }
}
